import { useFormikContext } from "formik";
import {
  Autocomplete,
  Box,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField
} from "@mui/material";
import { PLATFORM_TYPE_ICONS } from "./PlatformIcons";
import { normalizeTags } from "../utils/tags";

export type PlatformFormValues = {
  name: string;
  type: string;
  typeOption: string;
  model: string;
  description: string;
  color: string;
  tags: string[];
  latitude: string;
  longitude: string;
  altitude: string;
  heading: string;
  locationSet: boolean;
};

export const PRESET_PLATFORM_TYPES = ["Plane", "Helicopter", "Quadcopter", "Camera"];
export const OTHER_TYPE_OPTION = "Other";

const singleLineFieldSx = { "& .MuiOutlinedInput-root": { minHeight: 56 } };

const renderTypeOptionLabel = (option: string) => {
  const IconComponent = PLATFORM_TYPE_ICONS[option];
  return (
    <Box component="span" display="inline-flex" alignItems="center" gap={1}>
      {IconComponent ? <IconComponent fontSize="small" /> : null}
      <Box component="span">{option}</Box>
    </Box>
  );
};

const PlatformFormGeneral = () => {
  const { values, errors, touched, handleChange, handleBlur, setFieldValue, setFieldTouched } =
    useFormikContext<PlatformFormValues>();

  const showOtherTypeField = values.typeOption === OTHER_TYPE_OPTION;
  const showTypeSelectError = touched.type && Boolean(errors.type) && !showOtherTypeField;
  const tagsError = Array.isArray(errors.tags) ? errors.tags.join(", ") : errors.tags;

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-start">
        <TextField
          name="name"
          label="Name"
          value={values.name}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.name && Boolean(errors.name)}
          helperText={touched.name ? errors.name : undefined}
          sx={{
            width: { xs: "100%", sm: "80%" },
            flexBasis: { sm: "80%" },
            flexGrow: 1,
            ...singleLineFieldSx
          }}
        />
        <TextField
          name="color"
          label="Color"
          type="color"
          value={values.color}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.color && Boolean(errors.color)}
          helperText={touched.color ? errors.color : undefined}
          InputLabelProps={{ shrink: true }}
          sx={{
            width: { xs: "100%", sm: "10%" },
            flexBasis: { sm: "10%" },
            flexShrink: 0,
            ...singleLineFieldSx,
            "& .MuiInputBase-input": {
              padding: 0,
              height: 48,
              minWidth: 40,
              cursor: "pointer"
            }
          }}
        />
      </Stack>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-start">
        <FormControl
          error={showTypeSelectError}
          sx={{
            width: { xs: "100%", sm: "40%" },
            flexBasis: { sm: "40%" },
            flexShrink: 0,
            ...singleLineFieldSx,
            "& .MuiSelect-select": {
              display: "flex",
              alignItems: "center"
            }
          }}
        >
          <InputLabel id="platform-type-label">Type</InputLabel>
          <Select<string>
            labelId="platform-type-label"
            label="Type"
            value={values.typeOption}
            onChange={(event) => {
              const nextValue = event.target.value as string;

              if (nextValue === OTHER_TYPE_OPTION) {
                setFieldValue("typeOption", OTHER_TYPE_OPTION);
                const nextCustomType = values.typeOption === OTHER_TYPE_OPTION ? values.type : "";
                setFieldValue("type", nextCustomType, true);
              } else {
                setFieldValue("typeOption", nextValue);
                setFieldValue("type", nextValue, true);
              }

              setFieldTouched("type", true, false);
            }}
            onBlur={() => setFieldTouched("type", true, true)}
            displayEmpty
            fullWidth
            renderValue={(selected) => {
              if (!selected) {
                return "Select type";
              }
              return renderTypeOptionLabel(selected);
            }}
          >
            <MenuItem value="" disabled>
              Select type
            </MenuItem>
            {PRESET_PLATFORM_TYPES.map((option) => (
              <MenuItem key={option} value={option}>
                {renderTypeOptionLabel(option)}
              </MenuItem>
            ))}
            <MenuItem value={OTHER_TYPE_OPTION}>{renderTypeOptionLabel(OTHER_TYPE_OPTION)}</MenuItem>
          </Select>
          {showTypeSelectError ? <FormHelperText>{errors.type}</FormHelperText> : null}
        </FormControl>
        <TextField
          name="model"
          label="Model"
          fullWidth
          value={values.model}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.model && Boolean(errors.model)}
          helperText={touched.model ? errors.model : undefined}
          sx={{
            width: { xs: "100%", sm: "60%" },
            flexBasis: { sm: "60%" },
            ...singleLineFieldSx
          }}
        />
      </Stack>
      {showOtherTypeField ? (
        <TextField
          name="type"
          label="Custom type"
          fullWidth
          sx={{ flexGrow: 1, ...singleLineFieldSx }}
          value={values.type}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.type && Boolean(errors.type)}
          helperText={touched.type ? errors.type : undefined}
        />
      ) : null}
      <Autocomplete<string, true, false, true>
        multiple
        freeSolo
        options={[]}
        value={values.tags}
        onChange={(_, newValue) => setFieldValue("tags", normalizeTags(newValue))}
        onBlur={() => setFieldTouched("tags", true, true)}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => {
            const { key, ...chipProps } = getTagProps({ index });
            return <Chip key={key} {...chipProps} label={option} size="small" />;
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            name="tags"
            label="Tags"
            placeholder="Add tag and press Enter"
            fullWidth
            sx={singleLineFieldSx}
            onBlur={handleBlur}
            error={touched.tags && Boolean(tagsError)}
            helperText={touched.tags ? tagsError : undefined}
          />
        )}
      />
      <TextField
        name="description"
        label="Description"
        fullWidth
        multiline
        minRows={2}
        value={values.description}
        onChange={handleChange}
        onBlur={handleBlur}
        helperText={touched.description ? errors.description : undefined}
      />
    </Stack>
  );
};

export default PlatformFormGeneral;
